import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../lib/apiBase';

const API_BASE_URL = getApiBaseUrl();

const EnrollModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', skills: '', experience: '', message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errors, setErrors] = useState({});

  const handleBlur = (e) => {
    const { name, value, required } = e.target;
    if (required && !value) {
      setErrors(prev => ({ ...prev, [name]: 'This field is required' }));
    } else if (name === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrors(prev => ({ ...prev, [name]: 'Invalid email address' }));
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch(`${API_BASE_URL}/api/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Server error');
      setStatus('success');
      setForm({ name: '', email: '', phone: '', skills: '', experience: '', message: '' });
      setErrors({});
    } catch {
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto z-10 animate-fadeIn border border-slate-200">
        {/* Header */}
        <div className="bg-slate-50 rounded-t-2xl px-7 py-5 relative border-b border-slate-200">
          <button
            onClick={onClose}
            aria-label="Close enrollment form"
            className="absolute top-4 right-4 w-8 h-8 bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-slate-500 text-[11px] font-black uppercase tracking-[0.28em] block mb-1">Join Our Talent Network</span>
          <h2 className="text-2xl font-black text-primary">Enroll Now</h2>
          <p className="text-slate-600 text-sm mt-1">Submit your details and we&apos;ll reach out with matching opportunities.</p>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          {status === 'success' ? (
            <div className="text-center py-10 space-y-4">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-black text-primary">You're Enrolled!</h3>
              <p className="text-slate-500 text-sm">Our team has received your details and will be in touch soon.</p>
              <button
                onClick={onClose}
                className="mt-4 px-8 py-3 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-colors text-sm"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Full name *</label>
                  <input
                    name="name" value={form.name} onChange={handleChange} onBlur={handleBlur} required
                    placeholder="Jane Smith"
                    className={`w-full px-4 py-3 border ${errors.name ? 'border-red-300' : 'border-slate-300'} rounded-md text-sm text-slate-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition placeholder:font-normal placeholder:text-slate-300`}
                  />
                  {errors.name && <p className="text-red-500 text-[10px] mt-1 pl-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Email *</label>
                  <input
                    name="email" value={form.email} onChange={handleChange} onBlur={handleBlur} required type="email"
                    placeholder="jane@email.com"
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-red-300' : 'border-slate-300'} rounded-md text-sm text-slate-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition placeholder:font-normal placeholder:text-slate-300`}
                  />
                  {errors.email && <p className="text-red-500 text-[10px] mt-1 pl-1">{errors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Phone</label>
                  <input
                    name="phone" value={form.phone} onChange={handleChange} onBlur={handleBlur}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-sm text-slate-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition placeholder:font-normal placeholder:text-slate-300"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Years of experience</label>
                  <select
                    name="experience" value={form.experience} onChange={handleChange} onBlur={handleBlur}
                    className="w-full px-4 py-3 border border-slate-300 rounded-md text-sm text-slate-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition bg-white"
                  >
                    <option value="">Select...</option>
                    <option value="0-1">0–1 years</option>
                    <option value="1-3">1–3 years</option>
                    <option value="3-5">3–5 years</option>
                    <option value="5-10">5–10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Skills / Tech stack</label>
                <input
                  name="skills" value={form.skills} onChange={handleChange} onBlur={handleBlur}
                  placeholder="e.g. React, Node.js, SAP, AWS..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-md text-sm text-slate-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition placeholder:font-normal placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-2 block">Message</label>
                <textarea
                  name="message" value={form.message} onChange={handleChange} onBlur={handleBlur} rows={3}
                  placeholder="Tell us about yourself, your goals, or what type of role you're looking for..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-md text-sm text-slate-800 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition resize-none placeholder:font-normal placeholder:text-slate-300"
                />
              </div>

              {status === 'error' && (
                <p className="text-red-500 text-xs font-semibold bg-red-50 px-4 py-2 rounded-lg">
                  Something went wrong. Please try again or contact us directly.
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3.5 bg-primary hover:bg-secondary text-white font-black rounded-lg transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm tracking-[0.04em]"
              >
                {status === 'submitting' ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting...
                  </>
                ) : 'Submit enrollment'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollModal;
